import posthog from "posthog-js";
import { Component, type ErrorInfo, type ReactNode } from "react";

import Button from "./Button";
import { getErrorBoundaryCaptureProperties } from "~/lib/errorBoundaryCapture";

interface AppErrorBoundaryProps {
	children: ReactNode;
	/** Next.js route pattern, e.g. `/glossary/[slug]` */
	route: string;
	/** Current path; a change clears the error so navigation recovers */
	path: string;
}

interface AppErrorBoundaryState {
	hasError: boolean;
}

/**
 * Catches render errors in page content, reports them to PostHog with the
 * React component stack, and shows a reload prompt instead of a blank page.
 */
class AppErrorBoundary extends Component<
	AppErrorBoundaryProps,
	AppErrorBoundaryState
> {
	state: AppErrorBoundaryState = { hasError: false };

	static getDerivedStateFromError(): AppErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		posthog.captureException(
			error,
			getErrorBoundaryCaptureProperties({
				componentStack: info.componentStack,
				route: this.props.route,
				path: this.props.path,
				viewport:
					typeof window === "undefined"
						? undefined
						: { width: window.innerWidth, height: window.innerHeight },
			}),
		);
	}

	componentDidUpdate(prevProps: AppErrorBoundaryProps) {
		if (this.state.hasError && prevProps.path !== this.props.path) {
			this.setState({ hasError: false });
		}
	}

	render() {
		if (!this.state.hasError) return this.props.children;

		return (
			<div className="mx-auto my-16 flex max-w-md flex-col items-center gap-3 px-4 text-center">
				<p className="font-semibold text-lg text-slate-800 dark:text-white">
					Something went wrong loading this page.
				</p>
				<p className="text-slate-600 dark:text-slate-300">
					Reload to try again. If it keeps happening, let us know.
				</p>
				<Button type="button" onClick={() => window.location.reload()}>
					Reload Page
				</Button>
			</div>
		);
	}
}

export default AppErrorBoundary;
